import type { MemberOnboardingResponse, WelcomeTaskType } from '../types/types'

/** AUTO activity types completed by BE after like / comment / post / RSVP / profile. */
export type WelcomeAutoActivityTaskType = Extract<
  WelcomeTaskType,
  'LIKE_POST' | 'COMMENT_POST' | 'FIRST_POST' | 'RSVP_EVENT' | 'COMPLETE_PROFILE'
>

function withCompletedTasks(current: MemberOnboardingResponse, tasks: MemberOnboardingResponse['tasks']): MemberOnboardingResponse {
  const checklistComplete = tasks.length > 0 && tasks.every(task => task.completed)
  return { ...current, tasks, checklistComplete }
}

/**
 * Marks a member checklist step completed in the TanStack cache.
 * Used so the learner board ticks immediately, and again after GET if the
 * server still returns the pre-complete snapshot (POST/GET race).
 */
export function markMemberOnboardingStepComplete(
  current: MemberOnboardingResponse | undefined,
  stepId: string
): MemberOnboardingResponse | undefined {
  if (!current?.tasks?.length || !stepId) return current

  let changed = false
  const tasks = current.tasks.map(task => {
    if (task.id !== stepId || task.completed) return task
    changed = true
    return {
      ...task,
      completed: true,
      completedAt: task.completedAt || new Date().toISOString(),
    }
  })

  if (!changed) return current

  return withCompletedTasks(current, tasks)
}

/**
 * Optimistically ticks incomplete AUTO checklist steps of the given types.
 * Used after like/comment/post mutations while BE owns the real completion.
 */
export function markMemberOnboardingTasksCompleteByTypes(
  current: MemberOnboardingResponse | undefined,
  types: readonly WelcomeAutoActivityTaskType[]
): MemberOnboardingResponse | undefined {
  if (!current?.tasks?.length || !types.length) return current

  const typeSet = new Set<string>(types)
  let changed = false
  const tasks = current.tasks.map(task => {
    if (task.completed || task.trackingMode !== 'AUTO' || !typeSet.has(task.type)) return task
    changed = true
    return {
      ...task,
      completed: true,
      completedAt: task.completedAt || new Date().toISOString(),
    }
  })

  if (!changed) return current

  return withCompletedTasks(current, tasks)
}
